import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { ArrowUpDown, Download, Filter, Loader2, Search } from 'lucide-react'
import { toast } from 'sonner'
import { useMutation } from '@tanstack/react-query'
import type {
  BalanceChangeFilters,
  BalanceChangeQuery,
  WalletTransaction,
} from '@/types/balance'
import type { AxiosError } from 'axios'
import type { ErrorResponse } from '@/types/common'
import type { ExcelColumn } from '@/lib/excel-export'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PaginationNext, PaginationPrevious } from '@/components/ui/pagination'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import TitleHeader from '@/components/TitleHeader'
import {
  useBalanceChanges,
  useExportBalanceChanges,
} from '@/hooks/useBalanceChanges'
import { TIME_RANGE_OPTIONS, TRANSACTION_TYPES } from '@/types/balance'
import { ExcelExporter } from '@/lib/excel-export'
import { balanceService } from '@/services/balance'

export const Route = createFileRoute(
  '/_authenticated/activity/balance-changes/',
)({
  component: BalanceChangesManagement,
})

// Loading skeleton component for table rows
const TableSkeleton = () => (
  <>
    {[...Array(10)].map((_, i) => (
      <TableRow key={i}>
        <TableCell>
          <Skeleton className="h-4 w-8" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-16" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-6 w-20 rounded-full" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-6 w-20 rounded-full" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-6 w-20 rounded-full" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-32" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-24" />
        </TableCell>
      </TableRow>
    ))}
  </>
)

function BalanceChangesManagement() {
  const [searchFilters, setSearchFilters] = useState<BalanceChangeFilters>({
    memberId: '',
    username: '',
    reason: '',
    timeRange: '',
    transactionType: '',
    status: '',
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(20)
  const [appliedFilters, setAppliedFilters] =
    useState<BalanceChangeFilters>(searchFilters)

  // Convert filters to API query
  const apiQuery: BalanceChangeQuery = {
    page: currentPage,
    take: pageSize,
    sorts: 'createdAt.DESC',
    isSelectAll: false,
    memberId: appliedFilters.memberId || undefined,
    username: appliedFilters.username || undefined,
    reason: appliedFilters.reason || undefined,
    type: appliedFilters.transactionType || undefined,
    status: appliedFilters.status || undefined,
    // Add time range logic here if needed
  }

  // Fetch data from API
  const {
    data: balanceChangesResponse,
    isLoading: balanceChangesLoading,
    error: balanceChangesError,
  } = useBalanceChanges(apiQuery)

  // Mutations
  const exportBalanceChangesMutation = useExportBalanceChanges()

  // Be resilient to various response shapes
  const balanceChangesItems =
    (balanceChangesResponse as any)?.data?.items ??
    (balanceChangesResponse as any)?.items ??
    balanceChangesResponse ??
    []
  const balanceChanges: WalletTransaction[] = Array.isArray(balanceChangesItems)
    ? balanceChangesItems
    : []
  const pagination = (balanceChangesResponse as any)?.data?.meta ??
    (balanceChangesResponse as any)?.meta ?? {
      total: 0,
      page: currentPage,
      limit: pageSize,
      totalPages: 1,
      hasNext: false,
      hasPrev: false,
    }

  const handleSearch = useCallback(() => {
    setAppliedFilters(searchFilters)
    setCurrentPage(1)
  }, [searchFilters])

  const clearFilters = useCallback(() => {
    const emptyFilters: BalanceChangeFilters = {
      memberId: '',
      username: '',
      reason: '',
      timeRange: '',
      transactionType: '',
      status: '',
    }
    setSearchFilters(emptyFilters)
    setAppliedFilters(emptyFilters)
    setCurrentPage(1)
  }, [])

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  const mutationExport = useMutation({
    mutationFn: () => {
      return balanceService.getBalanceChanges({
        page: 1,
        take: 100,
        isSelectAll: true,
      })
    },
    onSuccess: (res: any) => {
      const data = res.items.map((item: any) => ({
        id: item.id.slice(-4),
        before: formatCurrency(item.balanceBeforeMinor, item.currency),
        change: formatCurrency(item.amountMinor, item.currency),
        current: formatCurrency(item.balanceAfterMinor, item.currency),
        time: formatDateTime(item.createdAt),
        reason: getTransactionTypeLabel(item.type),
      }))

      ExcelExporter.export(data, balanceChangesExcelColumns, {
        filename: `danh-sach-bien-dong-so-du-${new Date().toISOString().split('T')[0]}.xlsx`,
        sheetName: 'Danh sách biến động số dư',
      })
      toast.success('Export Excel thành công!')
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data.message || 'Export thất bại!')
    },
  })

  const balanceChangesExcelColumns: ExcelColumn[] = [
    {
      key: 'id',
      label: 'Tên đăng nhập',
    },
    {
      key: 'before',
      label: 'Số dư trước',
    },
    {
      key: 'change',
      label: 'Số dư thay đổi',
    },
    {
      key: 'current',
      label: 'Số dư hiện tại',
    },
    {
      key: 'time',
      label: 'Thời gian',
    },
    {
      key: 'reason',
      label: 'Lý do',
    },
  ]

  const handleExportBalanceChanges = () => {
    mutationExport.mutate()
  }

  const formatCurrency = (amountMinor: number, currency: string = 'vnd') => {
    const currencyUpper = currency.toUpperCase()

    if (currencyUpper === 'VND') {
      // VND has 0 decimals, amountMinor is already in VND
      return new Intl.NumberFormat('vi-VN').format(amountMinor) + ' đ'
    } else {
      // For other currencies, assume amountMinor is in cents
      const amount = amountMinor / 100
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyUpper === 'USDT' ? 'USD' : currencyUpper,
      })
        .format(amount)
        .replace('$', `${currencyUpper} `)
    }
  }

  const formatDateTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const time = date.toLocaleTimeString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    const dateStr = date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
    return `${time} - ${dateStr}`
  }

  const getTransactionTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      top_up: 'Nạp tiền',
      withdrawal: 'Rút tiền',
      payment: 'Thanh toán đơn hàng mua',
      refund: 'Hoàn tiền',
      bonus: 'Thưởng',
      penalty: 'Phạt',
      adjustment: 'Điều chỉnh số dư',
      commission: 'Hoa hồng',
    }
    return typeMap[type] || type
  }

  const headerActions = () => (
    <div className="flex gap-3">
      <Button
        variant="outline"
        className="flex items-center gap-2"
        onClick={handleExportBalanceChanges}
        disabled={exportBalanceChangesMutation.isPending}
      >
        {exportBalanceChangesMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        Xuất dữ liệu
      </Button>
    </div>
  )

  return (
    <div className="p-6">
      <TitleHeader
        title="Biến động số dư"
        subtitle="Chào mừng bạn đến với Trung tâm thông tin dữ liệu của bạn!"
        action={headerActions()}
      />

      <Card className="mt-6 shadow-none">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">
            Nhật ký thay đổi số dư
          </CardTitle>
        </CardHeader>

        <Separator />

        <CardContent className="p-0">
          <div className="filter mb-5 px-4">
            <div className="grid grid-cols-4 gap-4 mb-5">
              <Input
                placeholder="ID thành viên"
                value={searchFilters.memberId}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    memberId: e.target.value,
                  }))
                }
              />
              <Input
                placeholder="Tìm username"
                value={searchFilters.username}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    username: e.target.value,
                  }))
                }
              />
              <Select
                value={searchFilters.transactionType || undefined}
                onValueChange={(value) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    transactionType: value,
                  }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Lý do" />
                </SelectTrigger>
                <SelectContent>
                  {TRANSACTION_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={searchFilters.timeRange || undefined}
                onValueChange={(value) =>
                  setSearchFilters((prev) => ({ ...prev, timeRange: value }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Chọn thời gian" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_RANGE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Button
                variant="outline"
                className="mr-3"
                onClick={handleSearch}
                disabled={balanceChangesLoading}
              >
                {balanceChangesLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Lọc
              </Button>
              <Button variant="outline" className="mr-3" onClick={clearFilters}>
                <Filter className="h-4 w-4 mr-2" />
                Xóa bộ lọc
              </Button>
            </div>
          </div>

          <Table className="">
            <TableHeader className="bg-gray-100">
              <TableRow>
                <TableHead className="w-16">#</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>
                  <div className="flex items-center gap-2">
                    Số dư trước
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2">
                    Số dư thay đổi
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2">
                    Số dư hiện tại
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2">
                    Thời gian
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  </div>
                </TableHead>
                <TableHead>Lý do</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {balanceChangesLoading ? (
                <TableSkeleton />
              ) : balanceChangesError ? (
                <TableRow>
                  <TableCell colSpan={7} className="px-4 py-12 text-center">
                    <div className="text-red-600">
                      <p>Có lỗi xảy ra khi tải dữ liệu biến động số dư</p>
                      <Button
                        variant="outline"
                        onClick={() => window.location.reload()}
                        className="mt-4"
                      >
                        Thử lại
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : balanceChanges.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="px-4 py-12 text-center">
                    <p className="text-gray-500">
                      Không có dữ liệu biến động số dư
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                balanceChanges.map((transaction, index) => (
                  <TableRow key={transaction.id || index}>
                    <TableCell>
                      {(pagination.page - 1) * pagination.limit + index + 1}
                    </TableCell>
                    <TableCell className="font-medium">
                      UID{transaction.id.slice(-4)}
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-green-100 text-green-800 border-green-200">
                        {formatCurrency(
                          transaction.balanceBeforeMinor,
                          transaction.currency,
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                        {formatCurrency(
                          transaction.amountMinor,
                          transaction.currency,
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                        {formatCurrency(
                          transaction.balanceAfterMinor,
                          transaction.currency,
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {formatDateTime(transaction.createdAt)}
                    </TableCell>
                    <TableCell>
                      {getTransactionTypeLabel(transaction.type)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>

        {/* Summary Bar */}
        {/* <div className="px-6 py-4 bg-gray-50 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span className="text-sm font-medium">
                  Số dư trước: {formatCurrency(summary.totalPreviousBalance, summary.currency)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <span className="text-sm font-medium">
                  Số dư thay đổi: {formatCurrency(summary.totalBalanceChange, summary.currency)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span className="text-sm font-medium">
                  Số dư hiện tại: {formatCurrency(summary.totalCurrentBalance, summary.currency)}
                </span>
              </div>
            </div>
          </div>
        </div> */}

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6 px-6 pb-6">
          <div className="text-sm text-gray-500">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} results
          </div>
          {pagination.totalPages > 1 && (
            <div className="flex items-center gap-2">
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (pagination.hasPrev) handlePageChange(currentPage - 1)
                }}
                className={
                  !pagination.hasPrev ? 'pointer-events-none opacity-50' : ''
                }
              />
              <span className="text-sm font-medium px-2">
                {pagination.page} / {pagination.totalPages}
              </span>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (pagination.hasNext) handlePageChange(currentPage + 1)
                }}
                className={
                  !pagination.hasNext ? 'pointer-events-none opacity-50' : ''
                }
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
